import React, { useState, useEffect, useRef } from 'react';
import { LuAsterisk, LuChevronDown } from 'react-icons/lu';
import { ErrorMessage } from '../ErrorMessage';

type Option = {
  value: string | number;
  label: string;
};

type SelectDropdownProps = {
  id: string;
  className?: string;
  placeholder?: string;
  value: string | number;
  onChange: (value: string | number) => void;
  required?: boolean;
  options: Option[];
  label?: string;
  icon?: React.ComponentType;
  error?: string;
  darkMode?: boolean; // Add darkMode prop
};

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  id,
  className = '',
  placeholder,
  value,
  onChange,
  required = false,
  options,
  label,
  icon: Icon,
  error,
  darkMode = false, // Default to false
}) => {
  const [filter, setFilter] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // Set initial filter value based on selected value
  useEffect(() => {
    if (value && value !== -1) {
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        setFilter(selectedOption.label);
      }
    }
  }, [value, options]);

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) => option.label.toLowerCase().includes(filter.toLowerCase())),
    );
  }, [filter, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option: Option) => {
    onChange(option.value);
    setFilter(option.label);
    setIsDropdownOpen(false);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const selectedOptionLabel = options.find(opt => opt.value === value)?.label || '';

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      {label && (
        <label htmlFor={id} className={`flex text-xs font-[600] mb-1 transition-colors ${
          darkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {required && (
            <span className="text-red-600 top-[2px] relative">
              <LuAsterisk />
            </span>
          )}
          {label}
        </label>
      )}
      
      <div
        className={`flex items-center border rounded-md transition-colors ${
          error 
            ? 'border-red-500 dark:border-red-500' 
            : darkMode 
              ? 'border-gray-600 hover:border-gray-500' 
              : 'border-gray-300 hover:border-gray-400'
        } ${darkMode ? 'bg-neutral-700' : 'bg-white'}`}
      >
        {Icon && (
          <span className={`ml-3 text-lg transition-colors ${
            darkMode ? 'text-gray-400' : 'text-[#98A1B0]'
          }`}>
            <Icon />
          </span>
        )}
        
        <input
          id={id}
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onFocus={handleInputFocus}
          className={`focus:outline-none px-4 py-3 w-full text-md transition-colors ${
            darkMode 
              ? 'bg-neutral-700 text-white placeholder-gray-400' 
              : 'text-gray-700 placeholder-gray-400'
          } ${Icon ? 'pl-1' : 'pl-4'}`}
          placeholder={placeholder || (value && value !== -1 ? selectedOptionLabel : 'Select an option')}
        />
        
        <button 
          type="button" 
          className={`px-4 text-lg transition-colors ${
            darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-label="Toggle dropdown"
        >
          <LuChevronDown className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {isDropdownOpen && (
        <ul className={`absolute z-10 w-full mt-1 border rounded-md shadow-lg max-h-[10rem] overflow-auto transition-all ${
          darkMode 
            ? 'bg-neutral-800 border-gray-700' 
            : 'bg-white border-gray-300'
        }`}>
          {filteredOptions.map((option) => (
            <li
              key={option.value}
              className={`px-4 py-2 cursor-pointer transition-colors ${
                darkMode 
                  ? 'text-gray-200 hover:bg-neutral-700' 
                  : 'text-gray-700 hover:bg-gray-100'
              } ${option.value === value && (darkMode ? 'bg-neutral-700 text-white' : 'bg-blue-50 text-blue-700')}`}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </li>
          ))}
          {filteredOptions.length === 0 && (
            <li className={`px-4 py-2 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              No options found
            </li>
          )}
        </ul>
      )}
      
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

export default SelectDropdown;